import { ENVIRONMENT_INITIALIZER, Injectable, Injector, Provider } from '@angular/core';
import { RealtimeEventPayload, RealtimeService } from './realtime.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExportCreateEvent, RealtimeEventType } from '@flux/core/client';
import { PalantirToastService } from '@flux/design-system';
import { GetPreSignedGQL } from '@generated/graphql';
import { first } from 'rxjs';
import { MySpaceBadgesService } from '@pages/my-space/my-space-badges.service';

@Injectable({
  providedIn: 'root',
})
export class RealtimeGlobalEventListenerService {
  // ExportId, ToastId
  private exports: Record<string, string> = {};

  // BulkActionId, ToastId
  private bulkActions: Record<string, string> = {};

  public static EXPORT_TYPE_MAP: Record<ExportCreateEvent['data']['type'], string> = {
    cards: 'cards',
    cards_tasks: 'tarefas',
    cards_attachments: 'anexos',
    databases: 'banco de dados',
    reviews: 'aprovações',
    solicitations: 'solicitações',
  };

  public constructor(
    private readonly realtimeService: RealtimeService,
    private readonly injector: Injector,
  ) {
    this.realtimeService.eventReceived$
      .pipe(takeUntilDestroyed())
      .subscribe(this.handleEvent.bind(this));
  }

  public handleEvent(event: RealtimeEventPayload<RealtimeEventType>): void {
    switch (event.op) {
      case RealtimeEventType.ExportCreate:
        this.handleExportCreate(event as RealtimeEventPayload<RealtimeEventType.ExportCreate>);
        break;
      case RealtimeEventType.ExportLogCreate:
        this.handleExportLogCreate(
          event as RealtimeEventPayload<RealtimeEventType.ExportLogCreate>,
        );
        break;
      case RealtimeEventType.ExportUpdate:
        this.handleExportUpdate(event as RealtimeEventPayload<RealtimeEventType.ExportUpdate>);
        break;
      case RealtimeEventType.MySpaceMyCardsCreate:
        this.handleMySpaceTabEvent('my-cards');
        break;
      case RealtimeEventType.MySpaceMyTasksCreate:
        this.handleMySpaceTabEvent('my-tasks');
        break;
      case RealtimeEventType.MySpaceMySolicitationsCreate:
        this.handleMySpaceTabEvent('my-solicitations');
        break;
      case RealtimeEventType.MySpaceMyReviewsCreate:
        this.handleMySpaceTabEvent('my-reviews');
        break;
      case RealtimeEventType.BulkActionCreate:
        this.handleBulkActionCreate(
          event as RealtimeEventPayload<RealtimeEventType.BulkActionCreate>,
        );
        break;
      case RealtimeEventType.BulkActionLogCreate:
        this.handleBulkActionProgress(
          event as RealtimeEventPayload<RealtimeEventType.BulkActionLogCreate>,
        );
        break;
      case RealtimeEventType.BulkActionUpdate:
        this.handleBulkActionUpdate(
          event as RealtimeEventPayload<RealtimeEventType.BulkActionUpdate>,
        );
        break;
      default:
        break;
    }
  }

  private handleExportCreate(event: RealtimeEventPayload<RealtimeEventType.ExportCreate>): void {
    const palantirToastService = this.injector.get(PalantirToastService);

    const toastId = palantirToastService.showLoading(
      `Sua exportação de ${RealtimeGlobalEventListenerService.EXPORT_TYPE_MAP[event.data.data.type]} está sendo processada, aguarde...`,
      15000,
    );

    this.exports[event.data.data.id] = toastId;
  }

  private handleExportLogCreate(
    event: RealtimeEventPayload<RealtimeEventType.ExportLogCreate>,
  ): void {
    const palantirToastService = this.injector.get(PalantirToastService);
    palantirToastService.toastEmitter$.next({
      id: this.exports[event.data.data.id],
      message: event.data.data.message,
      theme: 'loading',
      timeout: 10000,
    });
  }

  private handleExportUpdate(event: RealtimeEventPayload<RealtimeEventType.ExportUpdate>): void {
    const palantirToastService = this.injector.get(PalantirToastService);
    const toastId = this.exports[event.data.data.id];
    if (toastId) {
      palantirToastService.close(toastId);
      Reflect.deleteProperty(this.exports, event.data.data.id);
    }

    if (event.data.data.status === 'completed') {
      const getPresignedUrl = this.injector.get(GetPreSignedGQL);

      palantirToastService.showSuccess(
        `Sua exportação de ${RealtimeGlobalEventListenerService.EXPORT_TYPE_MAP[event.data.data.type]} foi concluída! Clique para baixar.`,
        7000,
        {
          label: 'Baixar arquivo',
          action: () => {
            getPresignedUrl
              .fetch({ key: event.data.data.file_key! })
              .pipe(first())
              .subscribe((result) => {
                if (result.data?.getPreSigned) {
                  window.open(result.data.getPreSigned, '_blank');
                } else {
                  palantirToastService.showError(
                    'Não foi possível obter o link para download do arquivo.',
                  );
                }
              });
          },
          icon: 'download',
          closeAfterAction: false,
        },
      );

      return;
    }

    palantirToastService.showError(
      `Ocorreu um erro ao processar sua exportação de ${RealtimeGlobalEventListenerService.EXPORT_TYPE_MAP[event.data.data.type]}.`,
      7000,
      undefined,
    );
  }

  private handleMySpaceTabEvent(tab: 'my-reviews' | 'my-tasks' | 'my-cards' | 'my-solicitations') {
    const mySpaceBadgesService = this.injector.get(MySpaceBadgesService);
    mySpaceBadgesService.markTabAsUpdated(tab);
  }

  private handleBulkActionCreate(
    event: RealtimeEventPayload<RealtimeEventType.BulkActionCreate>,
  ): void {
    const palantirToastService = this.injector.get(PalantirToastService);
    const id = palantirToastService.showLoading(
      `Ação em massa está sendo processada, aguarde...`,
      30_000,
    );
    this.bulkActions[event.data.data.id] = id;
  }

  private handleBulkActionProgress(
    event: RealtimeEventPayload<RealtimeEventType.BulkActionLogCreate>,
  ): void {
    const palantirToastService = this.injector.get(PalantirToastService);

    const { message, status } = event.data.data;

    const isCompletedOrFailed = status === 'completed' || status === 'failed';

    palantirToastService.toastEmitter$.next({
      id: this.bulkActions[event.data.data.id],
      message: message,
      theme: status === 'completed' ? 'success' : status === 'failed' ? 'error' : 'loading',
      timeout: isCompletedOrFailed ? 10_000 : 30_000,
    });
  }

  private handleBulkActionUpdate(
    event: RealtimeEventPayload<RealtimeEventType.BulkActionUpdate>,
  ): void {
    const palantirToastService = this.injector.get(PalantirToastService);

    const status = event.data.data.status;
    if (status === 'failed') {
      palantirToastService.toastEmitter$.next({
        id: this.bulkActions[event.data.data.id],
        message: `Ação em massa falhou ao ser processada.`,
        theme: 'error',
        timeout: 10_000,
      });
      return;
    }

    const { failure_count, success_count } = event.data.data;

    palantirToastService.toastEmitter$.next({
      id: this.bulkActions[event.data.data.id],
      message: `Ação em massa processada com sucesso, ${success_count} itens processados com sucesso e ${failure_count} falhas.`,
      theme: 'success',
      timeout: 10_000,
    });
  }

  public static provider(): Provider {
    return {
      provide: ENVIRONMENT_INITIALIZER,
      useFactory: (realtimeService: RealtimeService, injector: Injector) => () =>
        new RealtimeGlobalEventListenerService(realtimeService, injector),
      deps: [RealtimeService, Injector],
      multi: true,
    };
  }
}
